import { z } from 'zod'
import { positionSourceSchema } from '../enums/position-source.enum.js'

/**
 * A point on the earth, with the axes NAMED.
 *
 * GeoJSON would be the obvious alternative and is deliberately not used here.
 * Its coordinate arrays are `[longitude, latitude]` — the opposite order to the
 * `lat, lng` every mapping library, URL and human uses — and that single
 * inversion is the most reliably recurring bug in geospatial code. A shape
 * whose fields say which is which cannot be got wrong, and costs only a few
 * bytes on a payload that is already small.
 *
 * Export to GeoJSON, if some external tool ever wants it, is a pure function
 * over this shape rather than a reason to store the footgun.
 */
export const geoPointSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
})

export type GeoPoint = z.infer<typeof geoPointSchema>

/**
 * One axis of a point already stored.
 *
 * The range is `geoPointSchema`'s, so a latitude here is the same thing as
 * a latitude on a live pin. A value outside that range becomes null for
 * this axis alone. A snapshot is frozen JSON, and the on-wire patch turns
 * a failed parse into null for the whole change — one old coordinate must
 * not do that. Writes still use `geoPointSchema`, which refuses the point.
 */
function recordedAxis(axis: z.ZodNumber) {
  // `.nullable()` first, so the fallback is one of the outputs. Zod only
  // types `.catch` as the schema's own output, and a bare number's is not null.
  return axis.nullable().catch(null)
}

/**
 * A coordinate pair already stored, including on a change snapshot and on
 * the map of a past visit.
 */
export const recordedGeoPointSchema = z.object({
  latitude: recordedAxis(geoPointSchema.shape.latitude),
  longitude: recordedAxis(geoPointSchema.shape.longitude),
})

/** A stored point that still has both axes, and so can be drawn. */
export function isRecordedPoint<
  T extends { latitude: number | null; longitude: number | null },
>(point: T): point is T & { latitude: number; longitude: number } {
  return point.latitude !== null && point.longitude !== null
}

/**
 * The smallest ring that encloses any area, and the largest we accept.
 *
 * The upper bound exists to keep a payload bounded and an editor responsive —
 * every vertex is a draggable DOM handle. Tracing a park perimeter by hand
 * lands in the tens of points; a few hundred is already far more detail than
 * grounds-maintenance work needs.
 */
export const BOUNDARY_MIN_VERTICES = 3
export const BOUNDARY_MAX_VERTICES = 500

/**
 * A site's outline: a single closed ring of points.
 *
 * Stored WITHOUT a repeated closing vertex — the ring is implicitly closed from
 * the last point back to the first. Storing the duplicate is a GeoJSON
 * convention that buys nothing here and invites an editor to render a
 * zero-length edge or let the two copies drift apart under editing.
 *
 * One ring, not many. A `Site` is one contiguous place by definition — an HOA
 * spanning several parks is several sites, not one site with several outlines —
 * so multi-polygon support would be modeling a case the domain says does not
 * arise. Holes (a lake, a leased building) are a real possibility worth
 * revisiting; they are absent because nothing needs them yet, not because the
 * shape forbids adding an `interiorRings` field later.
 */
export const siteBoundarySchema = z.object({
  vertices: z
    .array(geoPointSchema)
    .min(BOUNDARY_MIN_VERTICES)
    .max(BOUNDARY_MAX_VERTICES),
})

export type SiteBoundary = z.infer<typeof siteBoundarySchema>

/**
 * Meters of GPS uncertainty on a pin we already stored.
 *
 * Non-negative, and not capped: a stored fix is a fact, and a phone that
 * reported a very large radius should still be readable. Null is a
 * hand-placed pin, which has no GPS uncertainty to report.
 */
export const storedAccuracyMetersSchema = z.number().nonnegative().nullable()

/**
 * Meters of GPS uncertainty on the way in.
 *
 * Capped, so a bad client cannot store a nonsense radius. The same cap on
 * every write, whether the pin is a tree or a valve.
 */
export const accuracyMetersInputSchema = z.number().nonnegative().max(100_000)

/**
 * A pin: where something is, how sure the fix was, and whether a person
 * placed it.
 *
 * One schema for a tree, a meter, a timer, a valve, and a problem's spot.
 * The coordinate ranges are `geoPointSchema`'s, so those records cannot
 * disagree about what a latitude is.
 */
export const placedPointSchema = geoPointSchema.extend({
  /** Meters of GPS uncertainty when the fix was taken; null if placed by hand. */
  accuracyM: storedAccuracyMetersSchema,
  positionSource: positionSourceSchema,
})

export type PlacedPoint = z.infer<typeof placedPointSchema>

/**
 * The same pin on input. Accuracy is required (null is a real answer: placed
 * by hand) and capped. A tree update may still omit it; that schema wraps
 * `accuracyMetersInputSchema` itself rather than using this object.
 */
export const placedPointInputSchema = geoPointSchema.extend({
  /** Null for a hand-placed pin. */
  accuracyM: accuracyMetersInputSchema.nullable(),
  positionSource: positionSourceSchema,
})
